// This app's CRUD entities (platform D143): the whole of each feature's definition. The server
// registers them (`createApp({ crud })` in server.ts); they are read only through
// `backend/src/routing/store.ts`, written by the browser through `/api/crud/<id>`.
// Records live in the write database (`APP_DB_*`, platform D160, D162) — each table made by its
// migration in `db/migrations/`.
//
// OUTSIDE-PLATFORM: P5 no boolean, datetime or integer kind — Yes/No choices and ISO text instead.

import { defineEntity } from "@axny/platform-contracts";
import { FEATURE_ROUTING, FEATURE_ROUTING_ADMIN, FEATURE_ROUTING_WRITE } from "./reports";

// Read is `routing` (every Role: Viewer, Warehouse, Manager); a write is `routingWrite` (Warehouse and
// Manager) — the CRUD router gates POST / PUT / DELETE / fill on it (`crudWriteFeature`). Two
// entities a Viewer still writes, each for themselves only: `user-preference` and `app-user`.

export const ROUTING_STATUSES = [
  "Not routed",
  "Routing requested",
  "Routed",
  "Pre-routed",
  "Need info",
  "Need extension",
  "Cancelled",
] as const;
export type RoutingStatus = (typeof ROUTING_STATUSES)[number];

/** The lists a line's fields take their values from (`config/routing-lists.json` starts them). */
export const LIST_NAMES = [
  "Work needed",
  "Inventory notes",
  "Lead",
  // The PE ORDERS columns (2026-10-30)
  "PE Order Status",
  "PE Order Assigned to",
] as const;
export type ListName = (typeof LIST_NAMES)[number];
/** The carriers of pickups and shipments (spec §5 Lists): seeded from the carriers already used. */
export const CARRIER_LIST = "Carrier";
/**
 * Routing status, a managed list since 2026-10-28: each value has a category the rules read
 * (`shared/src/routing/routing-status.ts`); until a manager changes it, the starting seven.
 */
export const ROUTING_STATUS_LIST = "Routing status";

/**
 * A line someone has touched: its WMS # and the warehouse's fields. One line per WMS # (V7 step 2):
 * A2000 groups, the app never regroups. A line nobody has touched is worked out from the source
 * each time and saved on its first edit or approval. Its pick tickets as last approved are
 * `baseline1`–`3` (shared/src/routing/baseline.ts): one text over three fields; the warehouse's
 * fields never come from it. A line from before WMS grouping (`groupKey` not `WMS|…`) is read as
 * "Regrouped by WMS" and never written again (shared/src/routing/carry.ts).
 */
export const ROUTING_LINES = defineEntity({
  id: "routing-line",
  table: "routing_line",
  label: "Line",
  plural: "Lines",
  idPrefix: "RL",
  feature: FEATURE_ROUTING,
  writeFeature: FEATURE_ROUTING_WRITE,
  fields: [
    // WMS|<wms #> (V7 step 2); before it, corp|customer_code|div|start|cancel|wh
    { name: "groupKey", label: "Grouping key", kind: "text", max: 160 },
    { name: "grouping", label: "Grouping", kind: "choice", options: ["Automatic", "By hand"] },
    { name: "groupedBy", label: "Grouped by", kind: "text", max: 128, required: false },
    { name: "groupedAt", label: "Grouped at", kind: "text", max: 30, required: false },
    // YYYY-MM-DD or ASAP, so text
    { name: "readyDate", label: "READY DATE", kind: "text", max: 10, required: false },
    // Several values of its list, "; " between them (shared/src/routing/lists.ts). 012-list-category-work-needed.sql
    { name: "workNeeded", label: "Work Needed", kind: "text", max: 1000, required: false },
    { name: "productionNotes", label: "Production Notes", kind: "text", max: 200, required: false },
    // A value of the Routing status list (its category is what the rules read), so text.
    { name: "routingStatus", label: "Routing status", kind: "text", max: 200 },
    { name: "routingNotes", label: "Routing Notes", kind: "text", max: 500, required: false },
    { name: "routingRef", label: "Routing Reference", kind: "text", max: 100, required: false },
    { name: "schedShip", label: "SCHEDULED SHIP DATE", kind: "text", max: 10, required: false },
    // Entered in the app by the routing team (Isaac); never from A2000. 007-routing-line-wms.sql
    { name: "appointmentDate", label: "Appointment date", kind: "date", required: false },
    { name: "inventoryNotes", label: "Inventory Notes", kind: "text", max: 200, required: false },
    { name: "lead", label: "Lead", kind: "text", max: 100, required: false },
    { name: "timing", label: "TIMMING FOR REWORK", kind: "text", max: 200, required: false },
    { name: "notes", label: "INV.", kind: "text", max: 500, required: false },
    { name: "routedOn", label: "Routed on", kind: "date", required: false },
    // Split: its pick tickets came from other WMS lines, until approved (carry.ts)
    { name: "reviewStatus", label: "Review", kind: "choice", options: ["Not reviewed", "Approved", "Split"] },
    { name: "reviewedBy", label: "Reviewed by", kind: "text", max: 128, required: false },
    { name: "reviewedAt", label: "Reviewed at", kind: "text", max: 30, required: false },
    { name: "finishedStatus", label: "Finished", kind: "choice", options: ["Shipped", "Cancelled"], required: false },
    { name: "finishedAt", label: "Finished on", kind: "date", required: false },
    { name: "finishedBy", label: "Finished by", kind: "text", max: 128, required: false },
    { name: "finishedNote", label: "Finish note", kind: "text", max: 500, required: false },
    // NVARCHAR(4000) each (005-routing-line-baseline.sql); `text`, not `longText`, so every read of
    // the list carries them
    { name: "baseline1", label: "Pick tickets as approved (1)", kind: "text", max: 4000, required: false },
    { name: "baseline2", label: "Pick tickets as approved (2)", kind: "text", max: 4000, required: false },
    { name: "baseline3", label: "Pick tickets as approved (3)", kind: "text", max: 4000, required: false },
    // 007-routing-line-wms.sql: the WMS # (also in groupKey), and what a carry-over brought — the
    // values its sources disagreed on (JSON, carry.ts) and the lines and WMS #s they came from
    { name: "wmsNo", label: "WMS #", kind: "text", max: 40, required: false },
    { name: "carryConflicts", label: "Carry-over conflicts", kind: "text", max: 4000, required: false },
    { name: "carriedFrom", label: "Came from", kind: "text", max: 500, required: false },
    // 014-routing-line-approved-dates.sql: READY DATE and SCHEDULED SHIP DATE as approved, written
    // with the baseline; either differing now makes the line Updated (assemble.ts TEAM_REVIEW_FIELDS)
    { name: "approvedReadyDate", label: "READY DATE as approved", kind: "text", max: 10, required: false },
    { name: "approvedSchedShip", label: "SCHEDULED SHIP DATE as approved", kind: "text", max: 10, required: false },
    // 017-pe-orders.sql: the PE ORDERS columns (shared/src/routing/pe-order.ts). NULL is "never edited":
    // the prefill shows (PE Order Ship Date, PE Order Status). No datetime kind: Start and End are text
    // "YYYY-MM-DD HH:MM", local, as reviewedAt; no boolean: ASAP is 1 or NULL.
    { name: "peShipDate", label: "PE Order Ship Date", kind: "date", required: false },
    { name: "peShipAsap", label: "PE Order Ship Date ASAP", kind: "number", max: 1, required: false },
    { name: "pullGroup", label: "PE Order Pull Group #", kind: "text", max: 40, required: false },
    { name: "locQty", label: "PE Order Loc Qty", kind: "text", max: 40, required: false },
    { name: "peAssignedTo", label: "PE Order Assigned To", kind: "text", max: 500, required: false },
    { name: "peEstimate", label: "PE Order Estimate", kind: "number", required: false },
    { name: "peStatus", label: "PE Order Status", kind: "text", max: 200, required: false },
    { name: "peStartAt", label: "PE Order Start Time", kind: "text", max: 16, required: false },
    { name: "peEndAt", label: "PE Order End Time", kind: "text", max: 16, required: false },
    // One value per line: the Shipping schedule's PLTS too (its row's own plts is no longer written).
    { name: "pallets", label: "PE Order Pallets", kind: "number", required: false },
    { name: "peComments", label: "PE Order Comments", kind: "text", max: 500, required: false },
  ],
});

/**
 * A pick ticket on a line, with a copy of its source fields as last approved: the baseline for
 * UPDATED (old → new) and the History record, because A2000 drops a pick ticket once it ships.
 * One row per pick ticket — unique by hand in its migration. The $ value is not copied: this
 * entity's list is readable by everyone in the app, the $ value by managers only.
 */
export const LINE_PICK_TICKETS = defineEntity({
  id: "line-pick-ticket",
  table: "line_pick_ticket",
  label: "Pick ticket",
  plural: "Pick tickets",
  idPrefix: "LPT",
  feature: FEATURE_ROUTING,
  writeFeature: FEATURE_ROUTING_WRITE,
  parent: { entity: "routing-line", field: "lineId" },
  defaultSort: { column: "pickTicket", direction: "asc" },
  fields: [
    { name: "lineId", label: "Line", kind: "text", max: 20 },
    { name: "pickTicket", label: "PT #", kind: "text", max: 20 },
    { name: "wh", label: "WH", kind: "text", max: 10, required: false },
    { name: "orderNo", label: "Order #", kind: "text", max: 20, required: false },
    { name: "corp", label: "CO.", kind: "text", max: 10, required: false },
    { name: "customerCode", label: "Customer code", kind: "text", max: 20, required: false },
    { name: "customerName", label: "CUSTOMER", kind: "text", max: 100, required: false },
    { name: "div", label: "Div", kind: "text", max: 10, required: false },
    { name: "division", label: "DIVISION", kind: "text", max: 100, required: false },
    { name: "store", label: "Store", kind: "text", max: 40, required: false },
    { name: "customerPo", label: "PO", kind: "text", max: 400, required: false },
    { name: "ptDate", label: "RECEIVED DATE", kind: "date", required: false },
    { name: "startDate", label: "FIRST SHIP DATE", kind: "date", required: false },
    { name: "cancelDate", label: "CANCEL DATE", kind: "date", required: false },
    { name: "qty", label: "QTY", kind: "number", required: false },
    { name: "wmsShipment", label: "WMS", kind: "text", max: 40, required: false },
    { name: "routingOpenBdays", label: "Routing opens (business days)", kind: "number", required: false },
    { name: "routingDueBdays", label: "Route by (business days)", kind: "number", required: false },
    { name: "sourceLoadedAt", label: "Source loaded at", kind: "text", max: 30, required: false },
  ],
});

export const PICKUP_STATUSES = ["Scheduled", "Shipped", "Cancelled"] as const;
export type PickupStatus = (typeof PICKUP_STATUSES)[number];

/**
 * A pickup (spec §4 "Pickups"): one truck — date, App Time, carrier, door and PICK UP # /
 * SHIPMENT # / BOL #, typed by the team (nothing from A2000). Its lines are its shipping rows.
 * Never deleted: Shipped once every row shipped, Cancelled once none is left. 008-shipping.sql
 */
export const PICKUPS = defineEntity({
  id: "pickup",
  table: "pickup",
  label: "Pickup",
  plural: "Pickups",
  idPrefix: "PU",
  feature: FEATURE_ROUTING,
  writeFeature: FEATURE_ROUTING_WRITE,
  defaultSort: { column: "pickupDate", direction: "asc" },
  fields: [
    { name: "wh", label: "WH", kind: "text", max: 10, required: false },
    { name: "pickupDate", label: "Date", kind: "date" },
    // 7:30 or 14:00, so text
    { name: "appTime", label: "App Time", kind: "text", max: 10, required: false },
    { name: "carrier", label: "Carrier", kind: "text", max: 100, required: false },
    // The carrier as picked from A2000's or Deposco's list (`carriers.ts`); empty on one typed before. 013
    { name: "carrierCode", label: "Carrier code", kind: "text", max: 40, required: false },
    { name: "carrierSource", label: "Carrier source", kind: "choice", options: ["A2000", "Deposco"], required: false },
    { name: "door", label: "Door", kind: "text", max: 20, required: false },
    { name: "loadRef", label: "PICK UP # / SHIPMENT # / BOL #", kind: "text", max: 100, required: false },
    { name: "notes", label: "Notes", kind: "text", max: 500, required: false },
    { name: "status", label: "Status", kind: "choice", options: [...PICKUP_STATUSES] },
  ],
});

export const SHIPPING_ROW_STATUSES = ["On pickup", "Removed", "Shipped"] as const;
export type ShippingRowStatus = (typeof SHIPPING_ROW_STATUSES)[number];

/**
 * A line on a pickup (spec §4 "Shipping rows"): the Perris Shipping Schedule's typed columns.
 * Taken off its pickup it is Removed, never deleted. 008-shipping.sql
 */
export const SHIPPING_ROWS = defineEntity({
  id: "shipping-row",
  table: "shipping_row",
  label: "Shipping row",
  plural: "Shipping rows",
  idPrefix: "SR",
  feature: FEATURE_ROUTING,
  writeFeature: FEATURE_ROUTING_WRITE,
  parent: { entity: "pickup", field: "pickupId" },
  defaultSort: { column: "sortOrder", direction: "asc" },
  fields: [
    { name: "pickupId", label: "Pickup", kind: "text", max: 20 },
    { name: "lineId", label: "Line", kind: "text", max: 20 },
    { name: "wmsNo", label: "WMS #", kind: "text", max: 40, required: false },
    { name: "ctns", label: "CTNS", kind: "number", required: false },
    { name: "plts", label: "PLTS", kind: "number", required: false },
    { name: "loader", label: "Loader", kind: "text", max: 100, required: false },
    { name: "inTime", label: "In:", kind: "text", max: 10, required: false },
    { name: "outTime", label: "Out:", kind: "text", max: 10, required: false },
    { name: "trailer", label: "Trailer#", kind: "text", max: 40, required: false },
    { name: "wmsAsnTrip", label: "WMS / ASN / TRIP", kind: "text", max: 100, required: false },
    { name: "notes", label: "Notes", kind: "text", max: 500, required: false },
    { name: "sortOrder", label: "Order", kind: "number", required: false },
    { name: "status", label: "Status", kind: "choice", options: [...SHIPPING_ROW_STATUSES] },
  ],
});

/**
 * A shipped line (spec §4 "Shipments"): what left, with its shipping row's fields. Who marked it
 * is the line's `finishedBy`, when is the record's `created_at`: never typed. 008-shipping.sql
 */
export const SHIPMENTS = defineEntity({
  id: "shipment",
  table: "shipment",
  label: "Shipment",
  plural: "Shipments",
  idPrefix: "SH",
  feature: FEATURE_ROUTING,
  writeFeature: FEATURE_ROUTING_WRITE,
  defaultSort: { column: "shipDate", direction: "desc" },
  fields: [
    { name: "lineId", label: "Line", kind: "text", max: 20 },
    // Empty: shipped outside the schedule (parcel, customer pickup…)
    { name: "pickupId", label: "Pickup", kind: "text", max: 20, required: false },
    { name: "shipDate", label: "Ship date", kind: "date" },
    { name: "carrier", label: "Carrier", kind: "text", max: 100, required: false },
    // The carrier as picked from A2000's or Deposco's list (`carriers.ts`); empty on one typed before. 013
    { name: "carrierCode", label: "Carrier code", kind: "text", max: 40, required: false },
    { name: "carrierSource", label: "Carrier source", kind: "choice", options: ["A2000", "Deposco"], required: false },
    { name: "loadRef", label: "PICK UP # / BOL", kind: "text", max: 100, required: false },
    { name: "tracking", label: "Tracking #", kind: "text", max: 100, required: false },
    { name: "loader", label: "Loader", kind: "text", max: 100, required: false },
    { name: "trailer", label: "Trailer#", kind: "text", max: 40, required: false },
    { name: "door", label: "Door", kind: "text", max: 20, required: false },
    { name: "notes", label: "Notes", kind: "text", max: 500, required: false },
    { name: "ctns", label: "CTNS", kind: "number", required: false },
    { name: "plts", label: "PLTS", kind: "number", required: false },
    { name: "inTime", label: "In:", kind: "text", max: 10, required: false },
    { name: "outTime", label: "Out:", kind: "text", max: 10, required: false },
  ],
});

/** What a change entry says was done (`shared/src/routing/change-log.ts` words each one). */
export const CHANGE_ACTIONS = [
  "edit",
  "approve",
  "choose",
  "schedule",
  "add-line",
  "pickup",
  "remove",
  "ship",
  "finish",
  "restore",
  "list",
  "pre-route",
  // Admin → Routing rules' release review: a group's routing window reviewed (no record changed).
  "review",
  // Admin → Import: a run's start and end (its records are `{ partOf }`).
  "import",
] as const;
export type ChangeAction = (typeof CHANGE_ACTIONS)[number];

/**
 * The System log's readable copy of every save made in the app (Part C, P13): one per record a
 * person changed, written by the browser right after the save (`frontend/.../logged-write.ts`).
 * `audit_log` stays the full record; apps cannot read it (platform D165, OUTSIDE-PLATFORM: P13).
 * Created only: the server refuses an update or a delete (`backend/src/crud-seed.ts`, D171). When is
 * `created_at`; who is `personId` (checked against `created_by`) and `personName`. 008-shipping.sql
 */
export const CHANGE_ENTRIES = defineEntity({
  id: "change-entry",
  table: "change_entry",
  label: "Change",
  plural: "Changes",
  idPrefix: "CE",
  feature: FEATURE_ROUTING,
  writeFeature: FEATURE_ROUTING_WRITE,
  fields: [
    { name: "entity", label: "Entity", kind: "text", max: 40 },
    { name: "recordId", label: "Record", kind: "text", max: 20 },
    { name: "lineId", label: "Line", kind: "text", max: 20, required: false },
    { name: "action", label: "Action", kind: "choice", options: [...CHANGE_ACTIONS] },
    // JSON: [{ "f": field, "from": old, "to": new }] (change-log.ts)
    { name: "changes", label: "Fields", kind: "text", max: 4000, required: false },
    { name: "note", label: "Note", kind: "text", max: 500, required: false },
    // The line as it read when saved: "TJ MAXX · 2 PTs · PO 20 058230"
    { name: "lineText", label: "Line (as saved)", kind: "text", max: 200, required: false },
    { name: "personId", label: "Person (id)", kind: "text", max: 128 },
    { name: "personName", label: "Person", kind: "text", max: 128, required: false },
    { name: "appVersion", label: "App version", kind: "text", max: 40, required: false },
    // When, to the second (ISO, the browser's clock); the list's created_at was a day before D165.
    // Checked against that day (`toChangeView`). 009-change-entry-saved-at.sql
    { name: "savedAt", label: "Saved at", kind: "text", max: 30, required: false },
  ],
});

/**
 * A value of one of the lists (Work needed, Inventory notes, Lead, Carrier). Anyone adds; managers edit,
 * retire, delete and merge — in the UI only (OUTSIDE-PLATFORM: P3, one write feature for all three
 * operations). Unique on (list, value) by hand in its migration.
 */
export const LIST_VALUES = defineEntity({
  id: "list-value",
  table: "list_value",
  label: "List value",
  plural: "List values",
  idPrefix: "LV",
  feature: FEATURE_ROUTING,
  writeFeature: FEATURE_ROUTING_WRITE,
  defaultSort: { column: "value", direction: "asc" },
  fields: [
    { name: "list", label: "List", kind: "choice", options: [...LIST_NAMES, CARRIER_LIST, ROUTING_STATUS_LIST] },
    { name: "value", label: "Value", kind: "text", max: 200 },
    { name: "retired", label: "Retired", kind: "choice", options: ["No", "Yes"] },
    // Routing status only: what the value means to the rules. 012-list-category-work-needed.sql
    {
      name: "category",
      label: "Category",
      kind: "choice",
      options: ["Not routed", "Requested", "Routed", "On hold", "Cancelled"],
      required: false,
    },
  ],
});

export const PRE_ROUTE_SOURCES = ["A2000", "Deposco"] as const;
export type PreRouteSource = (typeof PRE_ROUTE_SOURCES)[number];
export const PRE_ROUTE_STATUSES = ["Waiting", "Moved", "Cancelled", "Shipped"] as const;
export type PreRouteStatus = (typeof PRE_ROUTE_STATUSES)[number];

/**
 * A pre-route (spec §3.12): routing known before the A2000 pick ticket exists. The order # is
 * A2000 CTRL_NO (7 digits; ORDER_NO is the customer PO). Waiting until its pick tickets are in
 * the WMS, then Moved onto their Worklist lines (`shared/src/routing/pre-route.ts`); Cancelled or
 * Shipped when A2000 closes the order with no pick ticket. Deleted only while Waiting
 * (`backend/src/crud-seed.ts`). The order's fields as added are kept for when the order leaves
 * `routing-orders` (closed over 30 days ago); the live order wins while it is there. 010-pre-route.sql
 */
export const PRE_ROUTES = defineEntity({
  id: "pre-route",
  table: "pre_route",
  label: "Pre-route",
  plural: "Pre-routes",
  idPrefix: "PR",
  feature: FEATURE_ROUTING,
  writeFeature: FEATURE_ROUTING_WRITE,
  defaultSort: { column: "appointmentDate", direction: "asc" },
  fields: [
    { name: "source", label: "Source", kind: "choice", options: [...PRE_ROUTE_SOURCES] },
    { name: "orderNo", label: "Order #", kind: "text", max: 20 },
    { name: "routingRef", label: "Routing ref / load ID", kind: "text", max: 100, required: false },
    { name: "appointmentDate", label: "Pickup appt", kind: "date", required: false },
    { name: "notes", label: "Notes", kind: "text", max: 500, required: false },
    { name: "status", label: "Status", kind: "choice", options: [...PRE_ROUTE_STATUSES] },
    // ISO: when a session claimed the move (pre-route.ts); with moveDone "No" a stale claim is taken again
    { name: "movedAt", label: "Moved at", kind: "text", max: 30, required: false },
    { name: "movedLineIds", label: "Moved to", kind: "text", max: 500, required: false },
    { name: "moveDone", label: "Move done", kind: "choice", options: ["No", "Yes"], required: false },
    { name: "addedBy", label: "Added by", kind: "text", max: 128, required: false },
    // A2000's word when it closed the order with no pick ticket: SHIP, CNCL, B-CNCL
    { name: "orderStatus", label: "A2000 status", kind: "text", max: 20, required: false },
    { name: "canceledOn", label: "Canceled on", kind: "date", required: false },
    { name: "cancelCode", label: "Cancel code", kind: "text", max: 20, required: false },
    // The order as added (routing-orders), shown once A2000 no longer lists it
    { name: "customerName", label: "Customer", kind: "text", max: 100, required: false },
    { name: "customerPo", label: "PO", kind: "text", max: 400, required: false },
    { name: "division", label: "Division", kind: "text", max: 100, required: false },
    { name: "qty", label: "Qty", kind: "number", required: false },
    { name: "startDate", label: "Start", kind: "date", required: false },
    { name: "cancelDate", label: "Cancel", kind: "date", required: false },
  ],
});

/**
 * A division's abbreviation (Admin → Divisions): the short name every page shows for an A2000
 * division, its full name on hover (`shared/src/routing/divisions.ts`). One per corp + div — unique
 * by hand in its migration. A division with none, or with "" (Reset to default), shows the first 7
 * characters of its name. Its list and every write are the Admin feature's — the API refuses the
 * rest, and `backend/src/crud-seed.ts` again; the pages get the abbreviations in their routing
 * payload (`backend/src/routing/store.ts`). Display only: A2000 data and other records keep the
 * full name. 011-division-abbrev.sql
 */
export const DIVISION_ABBREVS = defineEntity({
  id: "division-abbrev",
  table: "division_abbrev",
  label: "Division abbreviation",
  plural: "Division abbreviations",
  idPrefix: "DA",
  feature: FEATURE_ROUTING_ADMIN,
  defaultSort: { column: "division", direction: "asc" },
  fields: [
    { name: "corp", label: "CO.", kind: "text", max: 10 },
    { name: "div", label: "Div", kind: "text", max: 10 },
    { name: "division", label: "DIVISION", kind: "text", max: 100, required: false },
    { name: "abbreviation", label: "Abbreviation", kind: "text", max: 12, required: false },
    { name: "updatedBy", label: "Updated by", kind: "text", max: 128, required: false },
  ],
});

/**
 * A customer's shown name (Admin → Customers): the name every page shows for an A2000 customer or a
 * Deposco trading partner, the original on hover (`shared/src/routing/customer-names.ts`). One per
 * source + code — unique by hand in its migration; several customers may share one name. None, or ""
 * (Reset to default), is the source's own rule. Read and written by the Admin feature only, as a
 * division abbreviation; the pages get them in their routing payload. Display only.
 * 018-customer-name.sql
 */
export const CUSTOMER_NAMES = defineEntity({
  id: "customer-name",
  table: "customer_name",
  label: "Customer shown name",
  plural: "Customer shown names",
  idPrefix: "CN",
  feature: FEATURE_ROUTING_ADMIN,
  defaultSort: { column: "code", direction: "asc" },
  fields: [
    { name: "source", label: "Source", kind: "text", max: 10 },
    { name: "code", label: "Code", kind: "text", max: 40 },
    { name: "customerName", label: "Name", kind: "text", max: 200, required: false },
    { name: "shownName", label: "Shown as", kind: "text", max: 40, required: false },
    { name: "updatedBy", label: "Updated by", kind: "text", max: 128, required: false },
  ],
});

/**
 * A customer's routing rule (Admin → Routing rules): Window opens and Route by, in business days
 * before FIRST SHIP DATE and CANCEL DATE (`shared/src/routing/routing-rules.ts`), winning over
 * A2000's CUST_REF1/2 inside the app only — never written back. One per source + code — unique by
 * hand in its migration; a rule set on a shown-name group is one record per code. `noRule` is "No
 * routing rule" on purpose; `separate` keeps the code's window out of its group (both BIT, NOT NULL
 * default 0: number fields of max 1, CRUD has no boolean; every write sends both). Empty days are
 * only "no app days". No record: the fallback (the group's, A2000's). Read and written
 * by the Admin feature only, as a customer's shown name; the pages get them in their routing
 * payload. 019-routing-rule.sql
 */
export const ROUTING_RULES = defineEntity({
  id: "routing-rule",
  table: "routing_rule",
  label: "Routing rule",
  plural: "Routing rules",
  idPrefix: "RR",
  feature: FEATURE_ROUTING_ADMIN,
  defaultSort: { column: "code", direction: "asc" },
  fields: [
    { name: "source", label: "Source", kind: "text", max: 10 },
    { name: "code", label: "Code", kind: "text", max: 40 },
    { name: "customerName", label: "Name", kind: "text", max: 200, required: false },
    { name: "openBdays", label: "Window opens (business days)", kind: "number", max: 60, required: false },
    { name: "dueBdays", label: "Route by (business days)", kind: "number", max: 60, required: false },
    { name: "noRule", label: "No routing rule", kind: "number", max: 1 },
    { name: "separate", label: "Separate from group", kind: "number", max: 1 },
    { name: "notes", label: "Notes", kind: "text", max: 500, required: false },
    { name: "updatedBy", label: "Updated by", kind: "text", max: 128, required: false },
  ],
});

/**
 * Who looks after which customers (Admin → Customer assignments, `assignments.ts`): one label as the
 * team's sheet says it ("Macys/ Bloomingdale's"), the customers it covers as `<source>:<code>`
 * keys, and its person as Owner or Backup. Both start the Worklist and History on their customers.
 * Written by the Admin feature only (a `beforeWrite` hook too); never deleted: retired.
 * OUTSIDE-PLATFORM: P20 (an app's per-person settings). 015-customer-assignment-preference.sql
 */
export const CUSTOMER_ASSIGNMENTS = defineEntity({
  id: "customer-assignment",
  table: "customer_assignment",
  label: "Customer assignment",
  plural: "Customer assignments",
  idPrefix: "CA",
  feature: FEATURE_ROUTING_ADMIN,
  defaultSort: { column: "personName", direction: "asc" },
  fields: [
    // Lower-case: sign-in may give it in either case.
    { name: "personEmail", label: "Person's email", kind: "text", max: 200 },
    { name: "personName", label: "Person", kind: "text", max: 128 },
    // "" only on a person added with no customer yet (`isPersonOnly`): the column stays NOT NULL, "" fits it.
    { name: "label", label: "Customers", kind: "text", max: 200, required: false },
    // "A2000:ROSSSTORES; Deposco:BURL" — several, "; " between them; "" until a manager links them.
    { name: "customerKeys", label: "Linked codes", kind: "text", max: 2000, required: false },
    { name: "role", label: "Role", kind: "choice", options: ["Owner", "Backup"] },
    { name: "note", label: "Note", kind: "text", max: 200, required: false },
    { name: "retired", label: "Retired", kind: "choice", options: ["No", "Yes"] },
    { name: "updatedBy", label: "Updated by", kind: "text", max: 128, required: false },
  ],
});

/**
 * A person's default view of a page (the Worklist, History): its filters as the address keeps
 * them (`view-url.ts`) and its sort, applied on every fresh open on any computer. One per person
 * and page; written only by its person (a `beforeWrite` hook), and never in the System log.
 * No `writeFeature`: a Viewer saves their own default view too — it changes nothing anyone else sees.
 * OUTSIDE-PLATFORM: P20 (an app's per-person settings). 015-customer-assignment-preference.sql
 */
export const USER_PREFERENCES = defineEntity({
  id: "user-preference",
  table: "user_preference",
  label: "Saved view",
  plural: "Saved views",
  idPrefix: "UP",
  feature: FEATURE_ROUTING,
  defaultSort: { column: "personEmail", direction: "asc" },
  fields: [
    { name: "personEmail", label: "Person's email", kind: "text", max: 200 },
    { name: "page", label: "Page", kind: "choice", options: ["worklist", "history"] },
    // JSON of the address's v.* keys; "" for none (Remove my default).
    { name: "view", label: "Filters", kind: "text", max: 4000, required: false },
    // JSON of the sort, as saved-sort.ts keeps it; "" for none.
    { name: "sort", label: "Sort", kind: "text", max: 1000, required: false },
    { name: "updatedAt", label: "Saved at", kind: "text", max: 30, required: false },
  ],
});

/**
 * Someone who opened the routing pages (`app-user`): their sign-in email (lower-case), name, and when
 * first and last seen — one of the people Admin → Customer assignments → Add suggests. Written by the
 * person's own browser on a routing page, at most every 10 minutes (`app-user.tsx`); only their own
 * (a `beforeWrite` hook), never in the System log. No `writeFeature`: a Viewer's browser writes
 * their own record too, on their first visit — it is the people list, not a routing change.
 * OUTSIDE-PLATFORM: P20 (the platform has no user directory) and P17 (the server cannot write it
 * itself). 016-app-user.sql
 */
export const APP_USERS = defineEntity({
  id: "app-user",
  table: "app_user",
  label: "Person who signed in",
  plural: "People who signed in",
  idPrefix: "AU",
  feature: FEATURE_ROUTING,
  defaultSort: { column: "email", direction: "asc" },
  fields: [
    { name: "email", label: "Email", kind: "text", max: 200 },
    { name: "displayName", label: "Name", kind: "text", max: 128, required: false },
    { name: "firstSeen", label: "First seen", kind: "text", max: 30 },
    { name: "lastSeen", label: "Last seen", kind: "text", max: 30 },
  ],
});

/**
 * A run of Admin → Import (`workbook-import.ts`): its file, who started it and when, its status
 * (Running, Paused, Done, Failed), its choices, and its counts and summary — never the plan, which
 * the browser keeps (several hundred KB; a CRUD text is at most 4000 and a body 64 KB, P11). Resume
 * plans the file again and skips what is already there. Written by the Admin feature only (a
 * `beforeWrite` hook): its start and its end are the run's two System log entries.
 * 020-import-run.sql
 */
export const IMPORT_RUNS = defineEntity({
  id: "import-run",
  table: "import_run",
  label: "Workbook import",
  plural: "Workbook imports",
  idPrefix: "IR",
  feature: FEATURE_ROUTING_ADMIN,
  defaultSort: { column: "startedAt", direction: "desc" },
  fields: [
    { name: "fileName", label: "File", kind: "text", max: 200 },
    { name: "fileHash", label: "File hash", kind: "text", max: 64 },
    { name: "startedBy", label: "Started by", kind: "text", max: 128 },
    { name: "startedAt", label: "Started at", kind: "text", max: 30 },
    { name: "finishedAt", label: "Finished at", kind: "text", max: 30, required: false },
    { name: "status", label: "Status", kind: "text", max: 200 },
    { name: "cutoff", label: "Schedule cut-off", kind: "text", max: 10, required: false },
    { name: "tabs", label: "Tabs", kind: "text", max: 500 },
    { name: "counts", label: "Counts", kind: "text", max: 2000 },
    { name: "summary", label: "Summary", kind: "text", max: 4000 },
  ],
});
